import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './sidebar';
import WeeklyOrderModal from './modal_windows/WeeklyOrderModal';
import ProductionDeliveryModal from './modal_windows/ProductionDeliveryModal';
import AddToWaybillModal from './modal_windows/AddToWaybillModal';

import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  StatCard, RefreshButton, SearchField, FilterPill,
  StatusBadge, TableCard, LoadingState, EmptyState, BulkBar,
} from '../../ui/CrmPage';

const WAYBILL_CREATED_STATUS_ID = 1;
const DEFAULT_DELIVERY_AREA_ID = 1;

const getCurrentWeekNumber = () => {
  const currentDate = new Date();
  const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
  const days = Math.floor((currentDate - startOfYear) / 86400000);
  return Math.ceil(days / 7);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

const getStartOfWeek = (weekNumber) => {
  const startOfYear = new Date(new Date().getFullYear(), 0, 1);
  const days = (weekNumber - 1) * 7;
  const d = new Date(startOfYear);
  d.setDate(startOfYear.getDate() + days);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff + 1));
};

const getWeekDates = (start) => {
  const week = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    week.push(formatDate(d));
  }
  return week;
};

const WeeklyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState('all'); // 'all' | id
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showAddToWaybill, setShowAddToWaybill] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedWeek, setSelectedWeek] = useState(0);
  const [currentWeek] = useState(getCurrentWeekNumber());
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const weekNumber = currentWeek + weekOffset + selectedWeek;

  // Список регионов — грузим один раз
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_API_URL}v1/regions`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setRegions(Array.isArray(data) ? data : []);
      } catch { /* регионы не загрузились — фильтр останется только "Все" */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Регион фильтруем на клиенте, чтобы счётчики на пилюлях оставались
  // честными по всей неделе. Бекенд-параметр region_id поддерживается для
  // других потребителей API, но из UI мы его не шлём.
  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/week?week_number=${weekNumber}`);
      const data = await response.json();
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    } finally { setIsLoading(false); }
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, [weekNumber, reloadKey]);

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const order = orders.find(o => String(o.id) === String(orderId));
    if (!order) return;
    const updatedOrder = {
      delivery_address: order.delivery_address || '',
      delivery_date: order.delivery_date || new Date().toISOString().split('T')[0],
      id_status: newStatus,
      id_payment_status: order.id_payment_status || 1,
      total_amount: order.total_amount || 0,
      id_organization: order.id_organization,
      id_division: order.id_division || null,
      id_drivers: order.id_drivers || null,
      week_number: order.week_number || weekNumber,
      id_contracts: order.id_contracts,
      id_client: order.id_client,
      id_user: order.id_user,
      id_region: order.id_region,
    };
    const response = await fetch(`${process.env.REACT_APP_API_URL}v1/orders/${order.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedOrder),
    });
    if (!response.ok) console.error('Ошибка обновления');
  };

  const handleBulkUpdate = async (status) => {
    await Promise.all(selectedOrders.map(id => updateOrderStatus(id, status)));
    await fetchOrders();
    setSelectedOrders([]);
  };

  // === Путевые: «Создать путевой» и «К путевому» ===
  const handleCreateWaybill = async ({ driverId, vehicleId }) => {
    if (!selectedOrders.length) return;
    const dId = Number(driverId);
    const vId = vehicleId === '' || vehicleId == null ? null : Number(vehicleId);
    if (!Number.isInteger(dId) || dId <= 0) { alert('Выберите экспедитора.'); return; }
    // Регион путевого — берём id_region первого выбранного заказа.
    const first = orders.find((o) => selectedOrders.includes(o.id));
    const idRegions = Number(first?.id_region) || 5;
    setIsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}v1/waybills/with-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_waybillstatus: WAYBILL_CREATED_STATUS_ID,
          id_vehicles: vId,
          id_regions: idRegions,
          id_drivers: dId,
          id_delivery_areas: DEFAULT_DELIVERY_AREA_ID,
          order_ids: selectedOrders.map((id) => Number(id)),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { waybill, items } = await res.json();
      // Переводим заказы в статус «Создан наряд» (14).
      // В путь они уйдут уже из «Производство», когда соберут.
      await Promise.all(selectedOrders.map((id) => updateOrderStatus(id, 14)));
      setSelectedOrders([]);
      await fetchOrders();
      alert(`Создан путевой №${waybill.id} (${items.length} поз.).`);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setIsLoading(false);
      setShowDeliveryModal(false);
    }
  };

  const handleAddToWaybill = async (waybill) => {
    if (!selectedOrders.length || isLoading) return;
    const waybillId = Number(waybill?.id);
    if (!Number.isInteger(waybillId) || waybillId <= 0) { alert('Неверный путевой'); return; }
    setIsLoading(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}v1/waybills/${waybillId}/items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_ids: selectedOrders.map((id) => Number(id)) }),
      });
      if (!r.ok) throw new Error(await r.text());
      const result = await r.json();
      // Те же правила, что и при «Создать путевой» — статус «Создан наряд» (14).
      await Promise.all(selectedOrders.map((id) => updateOrderStatus(id, 14)));
      setSelectedOrders([]);
      await fetchOrders();
      alert(`Добавлено: ${result?.inserted ?? selectedOrders.length} в путевой №${waybillId}.`);
    } catch (e) {
      alert(`Ошибка: ${e.message}`);
    } finally {
      setIsLoading(false);
      setShowAddToWaybill(false);
    }
  };

  const weeks = useMemo(() => {
    const startOfCurrentWeek = getStartOfWeek(currentWeek + weekOffset);
    return Array.from({ length: 4 }, (_, i) => {
      const start = new Date(startOfCurrentWeek);
      start.setDate(start.getDate() + i * 7);
      return getWeekDates(start);
    });
  }, [weekOffset, currentWeek]);

  const summary = useMemo(() => ({
    total: orders.length,
    confirmed: orders.filter(o => /подтвержд/i.test(o.status_name || '')).length,
    production: orders.filter(o => /собира|производ|комплект/i.test(o.status_name || '')).length,
    carried: orders.filter(o => o.carried_over_from_week != null).length,
  }), [orders]);

  // Регионы, фактически встречающиеся в текущей выдаче — для счётчиков на пилюлях.
  const regionCounts = useMemo(() => {
    const map = new Map();
    orders.forEach(o => {
      const key = o.id_region != null ? String(o.id_region) : 'none';
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return orders.filter(o => {
      if (regionFilter !== 'all' && String(o.id_region) !== String(regionFilter)) return false;
      if (!term) return true;
      return (
        (o.client_name || '').toLowerCase().includes(term) ||
        (o.delivery_address || '').toLowerCase().includes(term) ||
        String(o.order_number || '').includes(term)
      );
    });
  }, [orders, searchTerm, regionFilter]);

  return (
    <div className="weekly-orders-basic-container crmpage">
      <div className="weekly-orders-sidebar-container">
        <Sidebar />
      </div>

      <div className="weekly-orders-basic-content crmpage__main">
        <header className="crmpage__head">
          <h1 className="crmpage__title">Заказы на неделю</h1>
        </header>

        <section className="crmpage__stats">
          <StatCard label="Всего"        value={summary.total}      accent="neutral" />
          <StatCard label="Подтверждены" value={summary.confirmed}  accent="success" />
          <StatCard label="В работе"     value={summary.production} accent="info" />
          <StatCard label="Перенесены"   value={summary.carried}    accent="warning" />
          <RefreshButton onClick={() => setReloadKey(k => k + 1)} />
        </section>

        {/* Неделя — селектор недель + поиск */}
        <section className="crmpage__filters" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="crmpage__pill"
            onClick={() => setWeekOffset(prev => Math.max(prev - 1, 0))}
            disabled={isLoading}
          ><ChevronLeftIcon sx={{ fontSize: 16 }} /></button>
          {weeks.map((week, index) => (
            <button key={index} type="button"
              className={`crmpage__pill ${selectedWeek === index ? 'is-active' : ''}`}
              onClick={() => setSelectedWeek(index)}
              disabled={isLoading}
            >{week[0]} – {week[6]}</button>
          ))}
          <button type="button" className="crmpage__pill"
            onClick={() => setWeekOffset(prev => prev + 1)}
            disabled={isLoading}
          ><ChevronRightIcon sx={{ fontSize: 16 }} /></button>
          <SearchField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Поиск: номер, клиент, адрес…"
          />
        </section>

        {/* Регионы */}
        <section className="crmpage__filters crmpage__filters--regions" style={{ flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`crmpage__pill ${regionFilter === 'all' ? 'is-active' : ''}`}
            onClick={() => setRegionFilter('all')}
            disabled={isLoading}
          >
            Все регионы
            <span className="crmpage__pill-count">{summary.total}</span>
          </button>
          {regions.map((r) => {
            const count = regionCounts.get(String(r.id)) || 0;
            const active = String(regionFilter) === String(r.id);
            return (
              <button
                key={r.id}
                type="button"
                className={`crmpage__pill ${active ? 'is-active' : ''}`}
                onClick={() => setRegionFilter(String(r.id))}
                disabled={isLoading}
              >
                {r.name}
                <span className="crmpage__pill-count">{count}</span>
              </button>
            );
          })}
        </section>

        <TableCard>
          {isLoading ? (
            <LoadingState text="Загружаем заказы недели…" />
          ) : filteredOrders.length === 0 ? (
            <EmptyState title="Нет заказов" text="На выбранной неделе пока нет заказов." />
          ) : (
            <div className="crmpage__table-wrap">
              <table className="crmpage__table">
                <colgroup>
                  <col style={{ width: 40 }} />
                  <col style={{ width: 80 }} />
                  <col />
                  <col />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 100 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="crmpage__th-check"></th>
                    <th>Номер</th>
                    <th>Клиент</th>
                    <th className="crmpage__hide-880">Адрес доставки</th>
                    <th className="crmpage__hide-1180">Договор</th>
                    <th>Статус</th>
                    <th className="crmpage__hide-1000">Доставка</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, idx) => {
                    const checked = selectedOrders.includes(order.id);
                    const carried = order.carried_over_from_week != null;
                    return (
                      <tr key={idx}
                        className={checked ? 'crmpage__row crmpage__row--selected' : 'crmpage__row'}
                        onClick={() => { setSelectedOrder(order); setIsModalOpen(true); }}
                      >
                        <td className="crmpage__td-check" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" className="crmpage__check"
                            checked={checked} onChange={() => toggleOrderSelection(order.id)} disabled={isLoading} />
                        </td>
                        <td>
                          <span className="crmpage__num">№{order.order_number}</span>
                          {carried && (
                            <span
                              className="crmpage__chip crmpage__chip--warning"
                              title={`Перенесён с недели ${order.carried_over_from_week}`}
                            >
                              перенос
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="crmpage__primary">{order.client_name || '—'}</div>
                          {order.region_name && (
                            <span className="crmpage__chip crmpage__chip--region">{order.region_name}</span>
                          )}
                        </td>
                        <td className="crmpage__muted crmpage__hide-880">{order.delivery_address || '—'}</td>
                        <td className="crmpage__muted crmpage__hide-1180">{order.contract_number || '—'}</td>
                        <td><StatusBadge name={order.status_name} /></td>
                        <td className="crmpage__date crmpage__hide-1000">{formatDate(order.delivery_date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </TableCard>

        <BulkBar count={selectedOrders.length} onClear={() => setSelectedOrders([])}>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => handleBulkUpdate(10)} disabled={isLoading}>
            <CancelOutlinedIcon sx={{ fontSize: 18 }} /> Отменить
          </button>
          <button className="crmpage__btn crmpage__btn--ghost" onClick={() => setShowAddToWaybill(true)} disabled={isLoading}>
            <PostAddOutlinedIcon sx={{ fontSize: 18 }} /> К путевому
          </button>
          <button className="crmpage__btn crmpage__btn--primary" onClick={() => setShowDeliveryModal(true)} disabled={isLoading}>
            <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} /> Создать путевой
          </button>
        </BulkBar>

        {isModalOpen && (
          <WeeklyOrderModal
            order={selectedOrder}
            onClose={() => { setIsModalOpen(false); setSelectedOrder(null); }}
          />
        )}
        {showDeliveryModal && (
          <ProductionDeliveryModal
            onClose={() => setShowDeliveryModal(false)}
            onConfirm={handleCreateWaybill}
            selectedOrders={selectedOrders}
          />
        )}
        {showAddToWaybill && (
          <AddToWaybillModal
            isOpen={showAddToWaybill}
            onClose={() => setShowAddToWaybill(false)}
            onConfirm={handleAddToWaybill}
          />
        )}
      </div>
    </div>
  );
};

export default WeeklyOrders;
